import React, { useState, useEffect } from 'react';
import {
  Modal,
  Select,
  DatePicker,
  InputNumber,
  Button,
  Card,
  message,
  Checkbox,
  Tooltip,
} from 'antd';
import {
  CreditCardOutlined,
  PlusOutlined,
  DeleteOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import CreditCardService from '../../service/credit-card-service/Credit-card.service';
import POSDeviceService, {
  OptimalPOSResponse,
} from '../../service/pos-device-service/POS-device.service';
import TransactionService, {
  WithdrawalServiceTransactionRequest,
} from '../../service/transaction-service/Transaction.service';
import { formatCurrency } from '../withdrawal-check/utils';
import { CreditCardResponse2 } from '../../model/Credit-card.model';
import useEmployee from '../../hook/useEmployee';

const { Option } = Select;

interface TransactionForm {
  id: number;
  creditCardId: number | null;
  posDeviceId: number | null;
  transactionDate: string;
  amount: number;
  feeForCustomer: number | null;
  customerType: '%' | 'vn';
  feeApplied: number;
  status: 'PENDING' | 'COMPLETED';
}

interface CreateWithdrawalModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateWithdrawalModal: React.FC<CreateWithdrawalModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [creditCards, setCreditCards] = useState<CreditCardResponse2[]>([]);
  const [posDevices, setPosDevices] = useState<{
    [key: number]: OptimalPOSResponse[];
  }>({});
  const [loadingPOS, setLoadingPOS] = useState<{ [key: number]: boolean }>({});
  const [loadingCards, setLoadingCards] = useState(false);
  // Rows submitted without a POS; their POS field shows the required-field error.
  const [missingPosIds, setMissingPosIds] = useState<number[]>([]);
  const { employee } = useEmployee();

  // Multiple transactions state
  const [transactions, setTransactions] = useState<TransactionForm[]>([
    {
      id: 1,
      creditCardId: null,
      posDeviceId: null,
      transactionDate: dayjs().format('YYYY-MM-DD'),
      amount: 0,
      feeForCustomer: null,
      customerType: '%',
      feeApplied: 0,
      status: 'PENDING',
    },
  ]);

  // Fetch credit cards when modal opens
  useEffect(() => {
    if (open) {
      fetchCreditCards();
    }
  }, [open]);

  const fetchCreditCards = async () => {
    setLoadingCards(true);
    try {
      const response = await CreditCardService.getCreditCardList();
      // Filter out cards with KHONG_LAM status (disabled cards)
      setCreditCards(response.filter((card) => card.newStatus !== 'KHONG_LAM'));
    } catch (error) {
      message.error('Không thể tải danh sách thẻ tín dụng');
    } finally {
      setLoadingCards(false);
    }
  };

  const fetchPOSDevices = async (
    transactionId: number,
    creditCardId: number
  ) => {
    setLoadingPOS((prev) => ({ ...prev, [transactionId]: true }));
    try {
      const transaction = transactions.find((t) => t.id === transactionId);
      const response = await POSDeviceService.getOptimalPOSDevicesForCard(
        creditCardId,
        transaction?.transactionDate
      );
      setPosDevices((prev) => ({ ...prev, [transactionId]: response }));
    } catch (error) {
      message.error('Không thể tải danh sách thiết bị POS');
      setPosDevices((prev) => ({ ...prev, [transactionId]: [] }));
    } finally {
      setLoadingPOS((prev) => ({ ...prev, [transactionId]: false }));
    }
  };

  const updateTransaction = (
    id: number,
    field: keyof TransactionForm,
    value: any
  ) => {
    setTransactions((prev) =>
      prev.map((t) => (t.id === id ? { ...t, [field]: value } : t))
    );
  };

  const handleCreditCardChange = (transactionId: number, value: number) => {
    updateTransaction(transactionId, 'creditCardId', value);
    updateTransaction(transactionId, 'posDeviceId', null);
    updateTransaction(transactionId, 'feeApplied', 0);

    // Clear POS devices for this transaction
    setPosDevices((prev) => ({ ...prev, [transactionId]: [] }));

    if (value) {
      fetchPOSDevices(transactionId, value);
    }
  };

  const applyDefaultFee = (transactionId: number) => {
    const transaction = transactions.find((t) => t.id === transactionId);
    const selectedCard = creditCards.find(
      (c) => c.id === transaction?.creditCardId
    );

    if (selectedCard?.defaultFeeRate && transaction?.customerType === '%') {
      updateTransaction(
        transactionId,
        'feeForCustomer',
        selectedCard.defaultFeeRate
      );
    }
  };

  const handlePOSChange = (transactionId: number, value: number) => {
    const selectedPOS = posDevices[transactionId]?.find(
      (device) => device.posId === value
    );
    updateTransaction(transactionId, 'posDeviceId', value);
    updateTransaction(transactionId, 'feeApplied', selectedPOS?.feeRate || 0);
  };

  const addTransaction = () => {
    const newId = Math.max(...transactions.map((t) => t.id)) + 1;
    setTransactions((prev) => [
      ...prev,
      {
        id: newId,
        creditCardId: null,
        posDeviceId: null,
        transactionDate: dayjs().format('YYYY-MM-DD'),
        amount: 0,
        feeForCustomer: null,
        customerType: '%',
        feeApplied: 0,
        status: 'PENDING',
      },
    ]);
  };

  const removeTransaction = (id: number) => {
    if (transactions.length > 1) {
      setTransactions((prev) => prev.filter((t) => t.id !== id));
      // Clean up POS devices for removed transaction
      setPosDevices((prev) => {
        const newDevices = { ...prev };
        delete newDevices[id];
        return newDevices;
      });
      setLoadingPOS((prev) => {
        const newLoading = { ...prev };
        delete newLoading[id];
        return newLoading;
      });
    }
  };

  const handleSubmit = async () => {
    // A POS is required. Without one the server used to answer with a misleading
    // "Bạn không được phép sử dụng thiết bị POS này".
    const withoutPos = transactions
      .filter((t) => !t.posDeviceId)
      .map((t) => t.id);
    setMissingPosIds(withoutPos);
    if (withoutPos.length > 0) {
      message.error('Vui lòng chọn thiết bị POS');
      return;
    }

    setLoading(true);
    try {
      if (!employee?.id) {
        message.error('Không tìm thấy thông tin nhân viên');
        return;
      }

      const requests = transactions.map((t) => ({
        creditCardId: t.creditCardId!,
        posDeviceId: t.posDeviceId!,
        transactionDate: t.transactionDate,
        amount: t.amount,
        transactionType: 'withdrawal' as const,
        feeForCustomer: t.feeForCustomer || 0,
        customerType: t.customerType,
        feeApplied: t.feeApplied,
        status: t.status,
        returnToCompany: false,
        transactionFor: 'withdrawal' as const,
      }));

      await TransactionService.createBulkWithdrawalTransactions(
        requests,
        employee.id
      );
      message.success(`Đã tạo ${transactions.length} giao dịch`);
      onSuccess();
      handleClose();
    } catch (error: any) {
      message.error('Không thể tạo giao dịch');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTransactions([
      {
        id: 1,
        creditCardId: null,
        posDeviceId: null,
        transactionDate: dayjs().format('YYYY-MM-DD'),
        amount: 0,
        feeForCustomer: null,
        customerType: '%',
        feeApplied: 0,
        status: 'PENDING',
      },
    ]);
    setPosDevices({});
    setLoadingPOS({});
    setMissingPosIds([]);
    onClose();
  };

  const formatCardDisplay = (card: CreditCardResponse2) => {
    const cardNumber = card.cardNumber?.slice(-4) || 'N/A';
    const holderName = card.cardHolderName || 'Chưa có tên';
    const cardType = card.cardType?.name || 'N/A';
    const bankName = card.bank?.name || 'N/A';
    return `***${cardNumber} - ${holderName} (${cardType} - ${bankName})`;
  };

  const formatPOSDisplay = (device: OptimalPOSResponse) => {
    const usageCount = device.transactionCount || 0;
    return `${device.deviceCode} - ${device.location} (${device.bank.name} | Phí: ${device.feeRate}% | Sử dụng: ${usageCount} lần)`;
  };

  const sortPOSDevices = (devices: OptimalPOSResponse[]) => {
    return [...devices].sort((a, b) => {
      // Ưu tiên theo số lần sử dụng: 0 lần lên đầu, >= 1 lần xuống cuối
      const aUsage = a.transactionCount || 0;
      const bUsage = b.transactionCount || 0;

      // Nếu một cái chưa sử dụng (0) và một cái đã sử dụng (>=1)
      if (aUsage === 0 && bUsage > 0) return -1;
      if (bUsage === 0 && aUsage > 0) return 1;

      // Nếu cả hai đều chưa sử dụng (0), sắp xếp theo phí thấp lên trên
      if (aUsage === 0 && bUsage === 0) {
        return a.feeRate - b.feeRate;
      }

      // Nếu cả hai đều đã sử dụng (>=1), sắp xếp theo số lần sử dụng tăng dần
      if (aUsage > 0 && bUsage > 0) {
        if (aUsage !== bUsage) {
          return aUsage - bUsage;
        }
        // Nếu số lần sử dụng bằng nhau, sắp xếp theo phí
        return a.feeRate - b.feeRate;
      }

      return 0;
    });
  };

  const isPosMissing = (transaction: TransactionForm) =>
    missingPosIds.includes(transaction.id) && !transaction.posDeviceId;

  const calculateBalance = (amount: number, feeApplied: number) => {
    return amount - (amount * feeApplied) / 100;
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <CreditCardOutlined className="text-blue-500" />
          <span className="text-sm">Tạo GD rút tiền</span>
        </div>
      }
      open={open}
      onCancel={handleClose}
      footer={null}
      width={1200}
      destroyOnClose
    >
      <div className="space-y-3">
        {transactions.map((transaction, index) => (
          <Card
            key={transaction.id}
            size="small"
            title={<span className="text-sm font-medium">GD #{index + 1}</span>}
            extra={
              transactions.length > 1 && (
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeTransaction(transaction.id)}
                  size="small"
                />
              )
            }
            className="border border-gray-200"
          >
            <div className="space-y-3">
              {/* Credit Card & Date & Status */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">
                    Thẻ tín dụng
                  </label>
                  <Select
                    placeholder="Chọn thẻ..."
                    value={transaction.creditCardId}
                    onChange={(value) =>
                      handleCreditCardChange(transaction.id, value)
                    }
                    className="w-full"
                    size="small"
                    showSearch
                    optionFilterProp="label"
                    loading={loadingCards}
                  >
                    {creditCards.map((card) => (
                      <Option
                        key={card.id}
                        value={card.id}
                        label={formatCardDisplay(card)}
                      >
                        {formatCardDisplay(card)}
                      </Option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">
                    Ngày GD
                  </label>
                  <DatePicker
                    value={dayjs(transaction.transactionDate)}
                    onChange={(date) =>
                      updateTransaction(
                        transaction.id,
                        'transactionDate',
                        date
                          ? date.format('YYYY-MM-DD')
                          : dayjs().format('YYYY-MM-DD')
                      )
                    }
                    className="w-full"
                    size="small"
                    format="DD/MM/YYYY"
                    disabledDate={(current) => {
                      if (!current) return false;
                      const today = dayjs().startOf('day');
                      const min = today.subtract(10, 'day');
                      const max = today.add(10, 'day');
                      return current.isBefore(min, 'day') || current.isAfter(max, 'day');
                    }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">
                    Trạng thái
                  </label>
                  <Select
                    value={transaction.status}
                    onChange={(value) =>
                      updateTransaction(transaction.id, 'status', value)
                    }
                    className="w-full"
                    size="small"
                    showSearch
                    optionFilterProp="label"
                  >
                    <Option value="PENDING" label="Đang chờ">
                      Đang chờ
                    </Option>
                    <Option value="COMPLETED" label="Hoàn thành">
                      Hoàn thành
                    </Option>
                  </Select>
                </div>
              </div>

              {/* POS Device */}
              <div>
                <label className="block text-xs font-medium mb-1">
                  Thiết bị POS
                </label>
                <Select
                  placeholder="Chọn thiết bị POS..."
                  value={transaction.posDeviceId}
                  onChange={(value) => handlePOSChange(transaction.id, value)}
                  className="w-full"
                  size="small"
                  status={isPosMissing(transaction) ? 'error' : undefined}
                  loading={loadingPOS[transaction.id]}
                  disabled={
                    !transaction.creditCardId || loadingPOS[transaction.id]
                  }
                  showSearch
                  optionFilterProp="label"
                >
                  {sortPOSDevices(posDevices[transaction.id] || []).map(
                    (device) => (
                      <Option
                        key={device.posId}
                        value={device.posId}
                        label={formatPOSDisplay(device)}
                      >
                        {formatPOSDisplay(device)}
                      </Option>
                    )
                  )}
                </Select>
                {isPosMissing(transaction) && (
                  <div className="text-xs text-red-500 mt-1">
                    Vui lòng chọn thiết bị POS
                  </div>
                )}
              </div>

              {/* Amount & Fees */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">
                    Số tiền (VND)
                  </label>
                  <InputNumber
                    value={transaction.amount}
                    onChange={(value) =>
                      updateTransaction(transaction.id, 'amount', value || 0)
                    }
                    className="w-full"
                    size="small"
                    formatter={(value) =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    }
                    parser={(value) =>
                      Number(value!.replace(/\$\s?|(,*)/g, ''))
                    }
                    min={0}
                  />
                </div>
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <label className="block text-xs font-medium">
                      Phí KH{' '}
                      {transaction.customerType === '%' ? '(%)' : '(VND)'}
                    </label>
                    {transaction.customerType === '%' &&
                      transaction.creditCardId &&
                      creditCards.find((c) => c.id === transaction.creditCardId)
                        ?.defaultFeeRate && (
                        <Tooltip
                          title={`Sử dụng phí mặc định: ${creditCards.find((c) => c.id === transaction.creditCardId)?.defaultFeeRate}%`}
                        >
                          <Button
                            type="text"
                            size="small"
                            icon={<ThunderboltOutlined />}
                            onClick={() => applyDefaultFee(transaction.id)}
                            className="text-blue-500 hover:text-blue-600 p-0 h-4 w-4"
                          />
                        </Tooltip>
                      )}
                  </div>
                  <InputNumber
                    value={transaction.feeForCustomer}
                    onChange={(value) =>
                      updateTransaction(
                        transaction.id,
                        'feeForCustomer',
                        value || 0
                      )
                    }
                    className="w-full"
                    size="small"
                    min={0}
                    max={transaction.customerType === '%' ? 100 : undefined}
                    step={transaction.customerType === '%' ? 0.1 : 1000}
                    formatter={
                      transaction.customerType === 'vn'
                        ? (value) =>
                            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                        : undefined
                    }
                    parser={
                      transaction.customerType === 'vn'
                        ? (value) => Number(value!.replace(/\$\s?|(,*)/g, ''))
                        : undefined
                    }
                  />
                  {/* Compact Checkboxes */}
                  <div className="flex gap-2 mt-1">
                    <Checkbox
                      checked={transaction.customerType === '%'}
                      onChange={() =>
                        updateTransaction(transaction.id, 'customerType', '%')
                      }
                      className="text-xs"
                    >
                      %
                    </Checkbox>
                    <Checkbox
                      checked={transaction.customerType === 'vn'}
                      onChange={() =>
                        updateTransaction(transaction.id, 'customerType', 'vn')
                      }
                      className="text-xs"
                    >
                      VND
                    </Checkbox>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">
                    Phí POS (%)
                  </label>
                  <InputNumber
                    value={transaction.feeApplied}
                    className="w-full"
                    size="small"
                    disabled
                    readOnly
                  />
                </div>
              </div>

              {/* Balance Display */}
              {transaction.amount > 0 && (
                <div className="bg-green-50 border border-green-200 rounded p-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-medium">Thực nhận:</span>
                    <span className="text-sm font-bold text-green-600">
                      {formatCurrency(
                        calculateBalance(
                          transaction.amount,
                          transaction.feeApplied
                        )
                      )}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </Card>
        ))}

        {/* Add Transaction Button */}
        <div className="text-center">
          <Button
            type="dashed"
            onClick={addTransaction}
            icon={<PlusOutlined />}
            size="small"
            className="w-full border border-dashed border-gray-300 hover:border-blue-400"
          >
            Thêm GD
          </Button>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-2 pt-2">
          <Button onClick={handleClose} disabled={loading} size="small">
            Hủy
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={loading}
            size="small"
            className="bg-blue-500 hover:bg-blue-600"
          >
            Tạo {transactions.length} GD
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CreateWithdrawalModal;
